import _ from "lodash";
import React from "react";
import {
  DeviceDesktopOutlined,
  LinkOutlined,
  GoogleDriveColored,
  OneDriveColored,
  AddOutlined,
} from "@toddle-edu/ds-icons";

export const TEMP_ID = "TEMP_";

export const METADATA_PROMPT_FIELDS = [
  "field",
  "type",
  "label",
  "config",
  "isRequired",
  "isDisabled",
];

export const ANIMATED_PLACEHOLDER_TEXTS = [
  "ai:exit_ticket_for_a_lesson_on_periodic_table_elements",
  "ai:practice_addition_of_fractions_with_unlike_denominators",
  "ai:walk_students_through_the_us_presidential_election_process",
  "ai:have_students_talk_about_their_dream_vacation_in_spanish",
  "ai:close_reading_of_macbeth_act_4_scene_3",
  "ai:check_student_understanding_about_the_causes_of_global_warming",
];

export const ALLOWED_FILE_TYPES = [
  "image/jpeg", // .jpeg
  "image/jpg", // .jpg
  "image/png", // .png
  "image/gif", // .gif,
  "image/webp", // .webp
  "text/plain", // .txt
  "text/csv", // .csv
  "application/pdf", // .pdf
  "application/vnd.google-apps.document", // Google Docs
  "application/vnd.google-apps.presentation", // Google Slides
  "application/vnd.google-apps.spreadsheet", // Google Sheets
  "application/msword", // .doc
  "application/vnd.ms-excel", // .xls
  "application/vnd.ms-powerpoint", // .ppt
  "application/vnd.ms-word.document.macroenabled.12", // .docm
  "application/vnd.ms-excel.sheet.macroenabled.12", // .xlsm
  "application/vnd.ms-powerpoint.presentation.macroenabled.12", // .pptm
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
];

export const CREATE_DROPDOWN_OPTIONS = [
  {
    key: "upload_device",
    label: "common:upload_device",
    icon: <DeviceDesktopOutlined size={"xx-small"} />,
  },
  {
    key: "add_a_link", // used "add_a_link" instead of "add_link" to trigger LinkifyModal of AI Chat instead of default CreateDropDown LinkModal
    label: "common:add_link",
    icon: <LinkOutlined size={"xx-small"} />,
  },
  {
    key: "add_google_drive",
    label: "common:add_google_drive",
    icon: <GoogleDriveColored size={"xx-small"} />,
  },
  {
    key: "add_one_drive",
    label: "common:add_one_drive",
    icon: <OneDriveColored size={"xx-small"} />,
  },
];

export const INITIAL_DIALOG_STATE = {
  action: "",
  params: {},
};

export const MODAL_ERRORS = {
  FILE_SIZE: "FILE_SIZE",
  FILE_COUNT: "FILE_COUNT",
  ZERO_TOKENS: "ZERO_TOKENS",
};

export const MAX_NUMBER_OF_FILES = 5;
export const MAX_SIZE_10_MEGA_BYTES = 10485760;

export const DIALOGS_INFO = {
  [MODAL_ERRORS.FILE_COUNT]: {
    title: ({ t }) => t("ai:file_limit_exceeded"),
    message: ({ t, label }) =>
      t("ai:file_count_exceeded_msg", { count: label }),
    buttonTwoText: ({ t }) => t("common:okay"),
  },
  [MODAL_ERRORS.FILE_SIZE]: {
    title: ({ t }) => t("ai:file_limit_exceeded"),
    message: ({ t, label }) => {
      if (_.size(label) === 1) {
        return t("ai:file_limit_exceeded_msg", { label: _.first(label) });
      } else {
        return t("ai:file_limit_exceeded_plural_msg", {
          label: _.map(label, name => `‘${name}’`).join(", "),
        });
      }
    },
    buttonTwoText: ({ t }) => t("common:okay"),
  },
  [MODAL_ERRORS.ZERO_TOKENS]: {
    title: ({ t }) => t("ai:unable_to_access_the_shared_link"),
    message: ({ t, label }) => {
      return t("ai:toddle_ai_could_not_retrieve_content_from_label", {
        label,
      });
    },
    buttonTwoText: ({ t }) => t("common:okay"),
  },
};

export const OPTIONS = {
  ADD_AUDIO: "ADD_AUDIO",
  ADD_IMAGE_FROM_CAMERA: "ADD_IMAGE_FROM_CAMERA",
  ADD_ATTACHMENT_FROM_DEVICE: "ADD_ATTACHMENT_FROM_DEVICE",
  ADD_MATH_EQUATION: "ADD_MATH_EQUATION",
  CODE_EDITOR: "CODE_EDITOR",
  ADD_GRAPH: "ADD_GRAPH",
  VIDEO_EXPLORER: "VIDEO_EXPLORER",
};

export const DEFAULT_FOOTER_OPTIONS = [
  {
    id: "add_attachment",
    key: OPTIONS.ADD_ATTACHMENT_FROM_DEVICE,
    icon: <AddOutlined />,
    tooltip: "common:attach_images",
    showTooltip: true,
  },
];

export const LEARNING_TOOLS_PLACEHOLDER = {
  document: "ai:toddle_doc_placeholder",
  code_editor: "ai:code_editor_placeholder",
  create_template_multipage: "ai:create_template_multipage_placeholder",
  video_explorer: "ai:video_explorer_placeholder",
  conversation_mode: "ai:conversation_mode_placeholder_v2",
};

export const LEARNING_TOOL_PROMPTS = {
  document: "aiTutor:learning_tool_prompt_document",
  code_editor: "aiTutor:learning_tool_prompt_code_editor",
  create_template_multipage:
    "aiTutor:learning_tool_prompt_create_template_multipage",
  video_explorer: "aiTutor:learning_tool_prompt_video_explorer",
  conversation_mode: "aiTutor:learning_tool_prompt_conversation_mode",
};

export const LATEX_DELIMITER_REGEX = /^\s*(\${1,2}[\s\S]+\${1,2}|\\\([\s\S]+\\\)|\\\[[\s\S]+\\\])\s*$/;

export const LATEX_COMMAND_REGEX = /(\\frac|\\sqrt|\\sum|\\int|\\begin\{|\\end\{|\\left|\\right|\\cdot|\\times|\\div|\\pi|\\theta|\\alpha|\\beta|\\gamma|\\delta|\\lambda|\\mu|\\nu|\\xi|\\rho|\\sigma|\\tau|\\phi|\\psi|\\omega|\\sin|\\cos|\\tan)/;
